import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, Loader2, MapPin, RefreshCw, Search, Info, ChevronLeft, ChevronRight, ArrowDownToLine, ArrowUpFromLine, Eraser, X, SendHorizonal } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'sonner';
import {
  pointVisitLogService,
  type PointVisitLogRow,
  type VisitEventType,
} from '../services/pointVisitLogService';

const formatDateTimeTr = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const eventLabel = (eventType: VisitEventType): { label: string; className: string; icon: any } => {
  if (eventType === 'arrival') {
    return { label: 'Varış', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300', icon: ArrowDownToLine };
  }
  return { label: 'Ayrılış', className: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300', icon: ArrowUpFromLine };
};

const pointTypeLabel = (pointType: string | null | undefined) => {
  if (pointType === 'loading') return 'Yukleme';
  if (pointType === 'unloading') return 'Boşaltma';
  if (pointType === 'intermediate') return 'Ara Teslim';
  if (pointType === 'delivery') return 'Ana Teslim';
  return 'Diğer';
};

const formatLocation = (latitude: number | null, longitude: number | null) => {
  if (latitude == null && longitude == null) return '-';
  return `${latitude != null ? Number(latitude).toFixed(5) : '-'}, ${longitude != null ? Number(longitude).toFixed(5) : '-'}`;
};

type SortDirection = 'asc' | 'desc';
type SortKey =
  | 'event_at'
  | 'event_type'
  | 'plate'
  | 'plan_kodu'
  | 'shipment_no'
  | 'point_name'
  | 'point_type'
  | 'driver_name'
  | 'driver_phone'
  | 'point_location'
  | 'location'
  | 'distance_km'
  | 'location_source'
  | 'owner_type';

export function PointVisitLogs() {
  const today = useMemo(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }, []);
  const monthStart = useMemo(() => `${today.slice(0, 8)}01`, [today]);

  const [logs, setLogs] = useState<PointVisitLogRow[]>([]);
  const [summaryRows, setSummaryRows] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'summary' | 'detail'>('summary');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [error, setError] = useState('');
  const [geofenceKm, setGeofenceKm] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [plate, setPlate] = useState('');
  const [orderCode, setOrderCode] = useState('');
  const [pointName, setPointName] = useState('');
  const [eventType, setEventType] = useState<'' | VisitEventType>('');
  const [ownerType, setOwnerType] = useState<'' | 'own' | 'spot'>('');
  const [pointType, setPointType] = useState<'' | 'loading' | 'unloading' | 'intermediate' | 'delivery'>('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortKey, setSortKey] = useState<SortKey>('event_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedProblemRow, setSelectedProblemRow] = useState<PointVisitLogRow | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyStatus, setReplyStatus] = useState<'in-progress' | 'resolved'>('in-progress');
  const [replying, setReplying] = useState(false);

  const loadLogs = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError('');
      const response = await pointVisitLogService.getLogs({
        from,
        to,
        plate: plate.trim() || undefined,
        orderCode: orderCode.trim() || undefined,
        pointName: pointName.trim() || undefined,
        eventType: eventType || undefined,
        ownerType: ownerType || undefined,
        pointType: pointType || undefined,
      });
      setLogs(Array.isArray(response?.data) ? response.data : []);
      setLoaded(true);
    } catch (err: any) {
      console.error('Nokta varış/ayrılış logları yüklenemedi:', err);
      setError(err?.message || 'Loglar alınırken bir hata oluştu');
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [from, to, plate, orderCode, pointName, eventType, ownerType, pointType]);

  const loadSummary = useCallback(async () => {
    try {
      const response: any = await pointVisitLogService.getSummary({ from, to, plate: plate.trim() || undefined, orderCode: orderCode.trim() || undefined, pointName: pointName.trim() || undefined, ownerType: ownerType || undefined, pointType: pointType || undefined });
      setSummaryRows(Array.isArray(response?.data) ? response.data : []);
    } catch (err: any) {
      console.error('Nokta özetleri yüklenemedi:', err);
      setError(err?.message || 'Nokta özetleri alınamadı');
    }
  }, [from, to, plate, orderCode, pointName, ownerType, pointType]);

  const runScan = useCallback(async () => {
    try {
      setScanning(true);
      setError('');
      const response = await pointVisitLogService.runScan();
      const result = response?.data;
      if (result?.geofenceKm != null) setGeofenceKm(Number(result.geofenceKm));
      return result;
    } catch (err: any) {
      console.error('Nokta varış/ayrılış taraması yapılamadı:', err);
      setError(err?.message || 'Tarama yapılırken bir hata oluştu');
      return null;
    } finally {
      setScanning(false);
    }
  }, []);

  const handleScanAndLoad = useCallback(async () => {
    await runScan();
    await Promise.all([loadLogs(), loadSummary()]);
  }, [runScan, loadLogs, loadSummary]);

  const handleCleanup = useCallback(async () => {
    if (!window.confirm('Geçmiş loglar yeniden düzenlenecek: aynı noktada ardışık tekrarlanan varış/ayrılış kayıtları silinecek, sevk ve plan kodları eklenecek. Onaylıyor musunuz?')) return;
    try {
      setCleaning(true);
      setError('');
      const response = await pointVisitLogService.cleanup();
      const result = response?.data;
      const message = result
        ? `${result.removed ?? 0} tekrar eden kayıt silindi, ${result.backfilled ?? 0} kayda sevk/plan kodu eklendi`
        : 'Geçmiş loglar düzenlendi';
      await loadLogs(true);
      toast.success(message);
    } catch (err: any) {
      console.error('Geçmiş loglar düzeltilemedi:', err);
      setError(err?.message || 'Geçmiş loglar düzeltilirken bir hata oluştu');
    } finally {
      setCleaning(false);
    }
  }, [loadLogs]);

  const openProblemModal = useCallback((row: PointVisitLogRow) => {
    setSelectedProblemRow(row);
    setReplyMessage('');
    setReplyStatus(row.waiting_ticket_status === 'resolved' ? 'resolved' : 'in-progress');
  }, []);

  const closeProblemModal = useCallback(() => {
    setSelectedProblemRow(null);
    setReplyMessage('');
    setReplyStatus('in-progress');
  }, []);

  const handleProblemReply = useCallback(async () => {
    if (!selectedProblemRow?.waiting_record_id) return;
    if (!replyMessage.trim()) {
      toast.error('Yanıt mesajı boş olamaz');
      return;
    }
    try {
      setReplying(true);
      await pointVisitLogService.replyToProblem(selectedProblemRow.waiting_record_id, {
        message: replyMessage.trim(),
        ticketStatus: replyStatus,
      });
      toast.success('Yanıt sürücüye gönderildi');
      await loadLogs(true);
      closeProblemModal();
    } catch (err: any) {
      console.error('Tahliye problemi yanıtı gönderilemedi:', err);
      toast.error(err?.message || 'Yanıt gönderilemedi');
    } finally {
      setReplying(false);
    }
  }, [selectedProblemRow, replyMessage, replyStatus, loadLogs, closeProblemModal]);

  useEffect(() => {
    void (async () => {
      const result = await runScan();
      if (result?.geofenceKm != null) setGeofenceKm(Number(result.geofenceKm));
       await Promise.all([loadLogs(), loadSummary()]);
    })();
  }, [runScan, loadLogs, loadSummary]);

  useEffect(() => {
    setPage(1);
  }, [from, to, plate, orderCode, pointName, eventType, ownerType, pointType, pageSize, sortKey, sortDirection]);

  const summary = useMemo(() => {
    return {
      total: logs.length,
      arrivals: logs.filter((row) => row.event_type === 'arrival').length,
      departures: logs.filter((row) => row.event_type === 'departure').length,
    };
  }, [logs]);

  const sortedLogs = useMemo(() => {
    const getValue = (row: PointVisitLogRow, key: SortKey) => {
      switch (key) {
        case 'event_at':
          return row.event_at ? new Date(row.event_at).getTime() : 0;
        case 'distance_km':
          return Number(row.distance_km || 0);
        case 'point_location':
          return formatLocation(row.point_latitude, row.point_longitude).toLocaleLowerCase('tr-TR');
        case 'location':
          return formatLocation(row.latitude, row.longitude).toLocaleLowerCase('tr-TR');
        case 'shipment_no':
          return String(row.shipment_no || row.order_code || '').toLocaleLowerCase('tr-TR');
        default:
          return String(row[key] || '').toLocaleLowerCase('tr-TR');
      }
    };

    return [...logs].sort((a, b) => {
      const left = getValue(a, sortKey);
      const right = getValue(b, sortKey);
      if (left < right) return sortDirection === 'asc' ? -1 : 1;
      if (left > right) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [logs, sortKey, sortDirection]);

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'event_at' ? 'desc' : 'asc');
  }, [sortKey]);

  const renderSortHeader = (label: string, key: SortKey, align: 'left' | 'right' = 'left') => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className={`flex w-full items-center gap-1 ${align === 'right' ? 'justify-end text-right' : 'justify-start text-left'} hover:text-slate-700 dark:hover:text-slate-200`}
    >
      <span>{label}</span>
      <span className="text-[10px] text-slate-400">{sortKey === key ? (sortDirection === 'asc' ? 'ASC' : 'DESC') : '--'}</span>
    </button>
  );

  const totalPages = Math.max(1, Math.ceil(sortedLogs.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const paginatedLogs = sortedLogs.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);

  const exportExcel = () => {
    const rows = logs.map((row) => ({
      'İşlem Tarihi': formatDateTimeTr(row.event_at),
      'İşlem Türü': row.event_type === 'arrival' ? 'Varış' : 'Ayrılış',
      'Plaka': row.plate,
      'Plan Kodu': row.plan_kodu || '-',
      'Sipariş No': row.shipment_no || row.order_code || '-',
      'Nokta': row.point_name || '-',
      'Nokta Türü': pointTypeLabel(row.point_type),
      'Sürücü': row.driver_name || '-',
      'Telefon': row.driver_phone || '-',
      'Konum': formatLocation(row.latitude, row.longitude),
      'Mesafe (km)': row.distance_km,
      'Konum Kaynağı': row.location_source === 'seyir' ? 'Seyir' : 'Mobil',
      'Araç Tipi': row.owner_type === 'own' ? 'Özmal' : 'Spot',
      'Nokta Koordinatı': formatLocation(row.point_latitude, row.point_longitude),
    }));

    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet['!cols'] = [{ wch: 17 }, { wch: 10 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 30 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 24 }];
    XLSX.utils.book_append_sheet(workbook, sheet, 'Nokta Logları');
    XLSX.writeFile(workbook, `nokta-varis-ayrilis-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const summaryCards = [
    { label: 'Toplam Log', value: String(summary.total), icon: MapPin, color: 'bg-blue-50 text-blue-700' },
    { label: 'Varış', value: String(summary.arrivals), icon: ArrowDownToLine, color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Ayrılış', value: String(summary.departures), icon: ArrowUpFromLine, color: 'bg-rose-50 text-rose-700' },
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Nokta Varış / Ayrılış Logları</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
              Boşaltma ve ara teslim noktaları ile spot araçlar için yükleme noktası varış ve ayrılış kayıtları.
            </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleScanAndLoad()}
            disabled={scanning || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <RefreshCw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
            Tara
          </button>
          <button
            type="button"
             onClick={() => void Promise.all([loadLogs(), loadSummary()])}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Getir
          </button>
          <button
            type="button"
            onClick={() => void handleCleanup()}
            disabled={cleaning || scanning || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
          >
            <Eraser className={`h-4 w-4 ${cleaning ? 'animate-pulse' : ''}`} />
            {cleaning ? 'Düzeltiliyor...' : 'Geçmişi Düzelt'}
          </button>
          <button
            type="button"
            onClick={exportExcel}
            disabled={logs.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excele Aktar
          </button>
        </div>
      </div>

      <div className="flex w-fit rounded-xl border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-[#141414]">
        <button type="button" onClick={() => setActiveTab('summary')} className={`rounded-lg px-4 py-2 text-sm font-semibold ${activeTab === 'summary' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>Özet</button>
        <button type="button" onClick={() => setActiveTab('detail')} className={`rounded-lg px-4 py-2 text-sm font-semibold ${activeTab === 'detail' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>Detaylı</button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
      <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-7">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Başlangıç Tarihi</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Bitiş Tarihi</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Plaka</label>
            <input type="text" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="Örn: 34 ABC 123" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Plan / Sipariş No</label>
            <input type="text" value={orderCode} onChange={(e) => setOrderCode(e.target.value)} placeholder="Plan veya sipariş no" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Nokta Adı</label>
            <input type="text" value={pointName} onChange={(e) => setPointName(e.target.value)} placeholder="Nokta adı" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">İşlem Türü</label>
            <select value={eventType} onChange={(e) => setEventType(e.target.value as '' | VisitEventType)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <option value="">Tümü</option>
              <option value="arrival">Varış</option>
              <option value="departure">Ayrılış</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Nokta Türü</label>
            <select value={pointType} onChange={(e) => setPointType(e.target.value as '' | 'loading' | 'unloading' | 'intermediate' | 'delivery')} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <option value="">Tümü</option>
              <option value="loading">Yükleme</option>
              <option value="unloading">Boşaltma</option>
              <option value="intermediate">Ara Teslim</option>
              <option value="delivery">Ana Teslim</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Araç Tipi</label>
            <select value={ownerType} onChange={(e) => setOwnerType(e.target.value as '' | 'own' | 'spot')} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <option value="">Tümü</option>
              <option value="own">Özmal</option>
              <option value="spot">Spot</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Geofence yarıçapı: <strong>{geofenceKm != null ? `${geofenceKm} km` : 'yükleniyor...'}</strong>.
            Araç noktanın bu çap alanına girdiğinde <strong>varış</strong>, bu çapın 2 katından ({geofenceKm != null ? `${Number(geofenceKm) * 2} km` : '...'}) uzaklaştığında <strong>ayrılış</strong> loglanır.
            Özmal araçlar için konum kaynağı Seyir, spot araçlar için mobil uygulama konumudur.
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {summaryCards.map((card) => (
          <div key={card.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
            <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${card.color}`}>
              <card.icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{card.label}</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      {activeTab === 'summary' ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#141414]">
          <div className="overflow-auto">
            <table className="w-full min-w-[1050px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400"><tr>
                <th className="px-4 py-3 font-semibold">Sipariş No</th><th className="px-4 py-3 font-semibold">Plan Kodu</th><th className="px-4 py-3 font-semibold">Nokta</th><th className="px-4 py-3 font-semibold">Nokta Türü</th><th className="px-4 py-3 font-semibold">Plaka / Sürücü</th><th className="px-4 py-3 font-semibold">İlk Varış</th><th className="px-4 py-3 font-semibold">Son Ayrılış</th><th className="px-4 py-3 font-semibold">Durum</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {loading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr> : summaryRows.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Özet kaydı bulunamadı</td></tr> : summaryRows.map((row) => {
                  const hasArrival = Boolean(row.first_unloading_arrival_at || row.loading_arrival_at);
                  const hasDeparture = Boolean(row.last_unloading_departure_at || row.loading_departure_at);
                  const arrival = row.point_type === 'loading' ? row.loading_arrival_at : row.first_unloading_arrival_at;
                  const departure = row.point_type === 'loading' ? row.loading_departure_at : row.last_unloading_departure_at;
                  const status = hasDeparture ? 'Ayrıldı' : hasArrival ? 'Vardı' : 'Kayıt yok';
                  return <tr key={`${row.shipment_id}-${row.point_key}`} className="hover:bg-slate-50 dark:hover:bg-slate-900"><td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{row.shipment_code || '-'}</td><td className="px-4 py-3 text-indigo-700 dark:text-indigo-300">{row.plan_kodu || '-'}</td><td className="px-4 py-3 text-slate-700 dark:text-slate-300">{row.point_name || '-'}</td><td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold dark:bg-slate-800">{pointTypeLabel(row.point_type)}</span></td><td className="px-4 py-3 text-slate-700 dark:text-slate-300">{row.plate || '-'} / {row.driver_name || '-'}</td><td className="px-4 py-3 whitespace-nowrap text-emerald-700 dark:text-emerald-300">{formatDateTimeTr(arrival)}</td><td className="px-4 py-3 whitespace-nowrap text-rose-700 dark:text-rose-300">{formatDateTimeTr(departure)}</td><td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-bold ${status === 'Ayrıldı' ? 'bg-rose-100 text-rose-800' : status === 'Vardı' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{status}</span></td></tr>;
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-200 px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">{summaryRows.length.toLocaleString('tr-TR')} sevkiyat/nokta özeti</div>
        </div>
      ) : (
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#141414]">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('İşlem Tarihi', 'event_at')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('İşlem Türü', 'event_type')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Plaka', 'plate')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Plan Kodu', 'plan_kodu')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Sipariş No', 'shipment_no')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Nokta', 'point_name')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Nokta Türü', 'point_type')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Sürücü', 'driver_name')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Telefon', 'driver_phone')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Nokta Koordinatı', 'point_location')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Konum', 'location')}</th>
                <th className="px-4 py-3 text-right font-semibold">{renderSortHeader('Mesafe (km)', 'distance_km', 'right')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Kaynak', 'location_source')}</th>
                <th className="px-4 py-3 font-semibold">{renderSortHeader('Araç Tipi', 'owner_type')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td>
                </tr>
              ) : !loaded ? (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td>
                </tr>
              ) : paginatedLogs.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-slate-400">Kayıt bulunamadı</td>
                </tr>
              ) : paginatedLogs.map((row) => {
                const event = eventLabel(row.event_type);
                const EventIcon = event.icon;
                return (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-600 dark:text-slate-300">{formatDateTimeTr(row.event_at)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${event.className}`}>
                        <EventIcon className="h-3.5 w-3.5" />
                        {event.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{row.plate || '-'}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-medium text-indigo-700 dark:text-indigo-300">{row.plan_kodu || '-'}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">
                      <div className="flex items-center gap-2">
                        <span>{row.shipment_no || row.order_code || '-'}</span>
                        {row.waiting_record_id ? (
                          <button
                            type="button"
                            onClick={() => openProblemModal(row)}
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/40"
                            title="Sürücü tahliye problemi bildirdi"
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.point_name || '-'}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{pointTypeLabel(row.point_type)}</span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.driver_name || '-'}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.driver_phone || '-'}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">{formatLocation(row.point_latitude, row.point_longitude)}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">{formatLocation(row.latitude, row.longitude)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-300">{row.distance_km != null ? Number(row.distance_km).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${row.location_source === 'seyir' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' : 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300'}`}>
                        {row.location_source === 'seyir' ? 'Seyir' : 'Mobil'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${row.owner_type === 'own' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' : 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300'}`}>
                        {row.owner_type === 'own' ? 'Özmal' : 'Spot'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-[#141414]">
          <div className="text-sm text-slate-600 dark:text-slate-400">
            {sortedLogs.length === 0
              ? 'Kayıt yok'
              : `${(clampedPage - 1) * pageSize + 1}-${Math.min(clampedPage * pageSize, sortedLogs.length)} / ${sortedLogs.length} kayıt`}
          </div>
          <div className="flex items-center gap-3">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>{size} / sayfa</option>
              ))}
            </select>
            <button type="button" onClick={() => setPage(clampedPage - 1)} disabled={clampedPage <= 1} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
              <ChevronLeft className="h-4 w-4" /> Önceki
            </button>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Sayfa {clampedPage} / {totalPages}</span>
            <button type="button" onClick={() => setPage(clampedPage + 1)} disabled={clampedPage >= totalPages} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
              Sonraki <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      )}

      {selectedProblemRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Tahliye Problemi</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Sipariş No: {selectedProblemRow.shipment_no || selectedProblemRow.order_code || '-'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeProblemModal}
                disabled={replying}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:hover:bg-slate-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl bg-rose-50 p-4 dark:bg-rose-950/30">
                  <div className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Sürücü Mesajı</div>
                  <div className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">{selectedProblemRow.waiting_reason || '-'}</div>
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{formatDateTimeTr(selectedProblemRow.waiting_reported_at)}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-900">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Detay</div>
                  <div className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">
                    <div>Sürücü: {selectedProblemRow.waiting_driver_name || selectedProblemRow.driver_name || '-'}</div>
                    <div>Nokta: {selectedProblemRow.waiting_location || selectedProblemRow.point_name || '-'}</div>
                    <div>Durum: {selectedProblemRow.waiting_ticket_status === 'resolved' ? 'Çözüldü' : selectedProblemRow.waiting_ticket_status === 'in-progress' ? 'İşlemde' : 'Açık'}</div>
                  </div>
                </div>
              </div>

              {selectedProblemRow.waiting_notes ? (
                <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-300">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Ek Not</div>
                  <div className="whitespace-pre-wrap">{selectedProblemRow.waiting_notes}</div>
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Ticket Durumu</label>
                  <select value={replyStatus} onChange={(e) => setReplyStatus(e.target.value as 'in-progress' | 'resolved')} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <option value="in-progress">İşlemde</option>
                    <option value="resolved">Çözüldü</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Yanıt</label>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={5}
                    placeholder="Sürücüye gönderilecek yanıtı yazın"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
              <button
                type="button"
                onClick={closeProblemModal}
                disabled={replying}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={() => void handleProblemReply()}
                disabled={replying}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {replying ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
                Yanıt Gönder
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
